import React, { useState, useContext, useEffect } from 'react';
import axios from 'axios';
import { AuthContext } from '@/App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;
const API = `${BACKEND_URL}/api`;

function Login() {
  const { login } = useContext(AuthContext);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [orgSettings, setOrgSettings] = useState({
    organization_name: 'Real Estate CRM',
    logo_base64: null
  });

  // Load organization settings on component mount
  useEffect(() => {
    loadOrganizationSettings();
  }, []);

  const loadOrganizationSettings = async () => {
    try {
      const response = await axios.get(`${API}/organization/settings`);
      if (response.data) {
        setOrgSettings(response.data);
      }
    } catch (error) {
      // Use defaults if no settings found
      console.log('Using default organization settings');
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Add timeout and retry logic for production reliability
      const response = await axios.post(`${API}/auth/login`, 
        { email, password },
        { timeout: 10000 } // 10 second timeout
      );
      login(response.data.access_token, response.data.user);
      toast.success('Login successful!');
    } catch (error) {
      console.error('Login error:', error);
      if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
        toast.error('Login timeout - please try again');
      } else {
        toast.error(error.response?.data?.detail || 'Login failed');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-4" style={{ background: 'linear-gradient(135deg, #0891b2 0%, #06b6d4 100%)' }}>
      <Card className="w-full max-w-md shadow-2xl" data-testid="login-card">
        <CardHeader className="space-y-1 text-center">
          {orgSettings.logo_base64 && (
            <div className="flex justify-center mb-4">
              <img 
                src={orgSettings.logo_base64}
                alt="Organization Logo"
                className="w-16 h-16 object-contain"
              />
            </div>
          )}
          <CardTitle className="text-3xl font-bold text-slate-800" style={{ fontFamily: 'Manrope' }}>
            {orgSettings.organization_name}
          </CardTitle>
          <CardDescription className="text-slate-600">Enter your credentials to access your account</CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleLogin} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                placeholder="name@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                data-testid="email-input"
                className="h-11"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password">Password</Label>
              <Input
                id="password"
                type="password"
                placeholder="••••••••"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                data-testid="password-input"
                className="h-11"
              />
            </div>
            <Button
              type="submit"
              className="w-full h-11 bg-cyan-600 hover:bg-cyan-700 text-white font-medium"
              disabled={loading}
              data-testid="login-button"
            >
              {loading ? 'Signing in...' : 'Sign In'}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}

export default Login;
